import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import contentService, { MenuItem } from '../services/contentService';
import { getImageUrl } from '../services/api';
import { translateWithCache } from '../services/translation';

const CategoryPage: React.FC = () => {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const { t, i18n } = useTranslation();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categoryName, setCategoryName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMenuItems = async () => {
      if (!categorySlug) return;
      
      try {
        // First, get all categories to find the correct category name
        const categories = await contentService.getMenuCategories();
        
        // Find the category that matches the slug
        const category = categories.find(cat => {
          const catSlug = cat.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
          return catSlug === categorySlug;
        });
        
        if (!category) {
          setError(t('menu.categoryNotFound'));
          setLoading(false);
          return;
        }
        
        const lang = (i18n.language || 'es').split('-')[0];

        let translatedCategoryName = category.name;
        let translatedItems;

        if (lang === 'en') {
          // Translate category name
          translatedCategoryName = await translateWithCache(category.name, 'es', 'en');

          // Fetch and translate menu items for this category
          const items = await contentService.getMenuItems({ category: category.name });
          translatedItems = await Promise.all(
            items.map(async (item) => ({
              ...item,
              name: await translateWithCache(item.name, 'es', 'en'),
              description: item.description ? await translateWithCache(item.description, 'es', 'en') : item.description,
            }))
          );
        } else {
          // Fetch menu items without translation
          translatedItems = await contentService.getMenuItems({ category: category.name });
        }

        setCategoryName(translatedCategoryName);
        setMenuItems(translatedItems);
      } catch (err: any) {
        console.error('Error fetching menu items:', err);
        setError(t('menu.failedToLoadItems'));
      } finally {
        setLoading(false);
      }
    };

    fetchMenuItems();
  }, [categorySlug, i18n.language, t]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box component="section" sx={{ position: 'relative' }}>
      {/* Background coffee elements */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        opacity: 0.05,
        zIndex: -1,
      }}>
        <Box>
          <img 
            src="/images/bg-coffee-elements.svg" 
            alt="" 
            style={{ width: '800px', height: '100%' }}
          />
        </Box>
      </Box>

      {/* Background gradient */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(to bottom, transparent, #FFF8E5)',
        opacity: 0.6,
        zIndex: -1,
      }} />

      {/* Header */}
      <Container maxWidth="lg" sx={{ pt: 10, px: { xs: 3, sm: 0, md: 0 } }}>


        {/* Back button */}
        <Box sx={{ mb: 4 }}>
          <Link
            component={RouterLink}
            to="/menu"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            <ArrowBackIcon sx={{ mr: 1 }} />
            {t('menu.backToMenu')}
          </Link>
        </Box>

        {/* Category title */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h1"
            sx={{
              fontFamily: '"Bebas Neue", sans-serif',
              textTransform: 'uppercase',
              color: '#513628',
              mb: 2,
            }}
          >
            {categoryName}
          </Typography>
        </Box>

        {/* Menu items */}
        {menuItems.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary">
              {t('menu.noItemsAvailable')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {menuItems.map((item) => (
              <Grid item xs={12} md={6} key={item.id}>
                <Card sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}>
                  {item.image_url && (
                    <Box
                      component="img"
                      src={getImageUrl(item.image_url)}
                      alt={item.name}
                      sx={{
                        width: '100%',
                        height: 200,
                        objectFit: 'cover',
                        borderTopLeftRadius: '16px',
                        borderTopRightRadius: '16px',
                      }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography
                        variant="h4"
                        component="h3"
                        sx={{
                          fontFamily: '"Bebas Neue", sans-serif',
                          fontWeight: 400,
                          color: '#513628',
                          flex: 1,
                        }}
                      >
                        {item.name}
                      </Typography>
                      {!item.has_options && item.price != null && (
                        <Typography
                          variant="h5"
                          sx={{
                            fontFamily: '"Bebas Neue", sans-serif',
                            fontWeight: 700,
                            color: '#F89C24',
                            ml: 2,
                          }}
                        >
                          ${item.price.toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.6,
                        fontSize: '1.1rem',
                      }}
                    >
                      {item.description}
                    </Typography>
                    
                    {/* Display options if available */}
                    {item.has_options && item.options && item.options.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          Opciones:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {item.options.map((option) => (
                            <Box 
                              key={option.id} 
                              sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                p: 1,
                                borderBottom: '1px dashed rgba(0,0,0,0.1)'
                              }}
                            >
                              <Typography variant="body2">{option.name}</Typography>
                              <Typography variant="body2" sx={{ color: '#F89C24', fontWeight: 'bold' }}>
                                ${option.price.toFixed(2)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                    {item.is_featured && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor: '#F89C24',
                            color: 'white',
                            px: 2,
                            py: 0.5,
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                          }}
                        >
                          Nuevo
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default CategoryPage;